import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      // Cookie is sent automatically; JWT bearer also works
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("chulha_token");
      setUser(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Legacy admin password login (kept for admin only)
  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("chulha_token", data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  // OTP-based registration/login — uses the new /auth/send-otp + /auth/verify-otp + /auth/resend-otp endpoints.
  // Backend normalises whatever format the user typed; frontend never talks to MSG91 directly.
  const sendOtp = async (mobile, intent) => {
    try {
      const { data } = await api.post("/auth/send-otp", { mobile, intent });
      return { ok: true, ...data };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const verifyOtp = async (payload) => {
    try {
      // payload = { mobile, otp, intent, [name], [email] }
      const { data } = await api.post("/auth/verify-otp", payload);
      if (data.token) localStorage.setItem("chulha_token", data.token);
      setUser(data.user);
      return { ok: true, user: data.user, role: data.role };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const resendOtp = async (mobile) => {
    try {
      const { data } = await api.post("/auth/resend-otp", { mobile });
      return { ok: true, ...data };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  // Google session exchange (called by AuthCallback after Emergent redirects back with fragment)
  const exchangeGoogleSession = async (session_id) => {
    try {
      const { data } = await api.post("/auth/google/session", { session_id });
      if (data.token) localStorage.setItem("chulha_token", data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("chulha_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, sendOtp, verifyOtp, resendOtp, exchangeGoogleSession, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
